import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import Table from '../common/Table';
import { FaEdit, FaTrash } from 'react-icons/fa';
import AddPaymentType from './AddPaymentType';
import { apiGet } from '../../api/apiClient';
import apiClient from '../../api/apiClient';
import { toastSuccess, toastError, toastConfirm } from '../common/toast';
import '../common/rtCommon.css';
import './PaymentTypesList.css';
import RTPageLayout from '../common/RTPageLayout';

const PaymentTypesList = ({ embedded = false }) => {
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [showAddEdit, setShowAddEdit] = useState(false);
  const [editType, setEditType] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchPaymentTypes();
  }, []);

  const fetchPaymentTypes = async () => {
    try {
      setLoading(true);
      const response = await apiGet('/payments/types');
      // Access response.data and ensure it's an array
      const data = response?.data || response || [];
      const paymentTypesArray = Array.isArray(data) ? data : [];

      // Filter to show only active payment types (case-insensitive)
      const activePaymentTypes = paymentTypesArray.filter(
        (type) => type.status && type.status.toLowerCase() === 'active',
      );

      setPaymentTypes(activePaymentTypes);
      setError('');
    } catch (err) {
      console.error('Error fetching payment types:', err);
      setError('Failed to load payment types');
      toastError('Failed to load payment types');
      setPaymentTypes([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditType(null);
    setShowAddEdit(true);
  };

  const handleEdit = (paymentType) => {
    setEditType(paymentType);
    setShowAddEdit(true);
  };

  const handleDelete = async (paymentTypeId, paymentTypeName) => {
    const confirmed = await toastConfirm(
      true,
      `Delete payment type "${paymentTypeName}"?`,
    );
    if (!confirmed) return;

    try {
      setDeleting(true);
      await apiClient.post(`/payments/types/${paymentTypeId}/delete`, {});
      fetchPaymentTypes();
      toastSuccess('Payment type deleted successfully');
    } catch (err) {
      console.error('Error deleting payment type:', err);
      setError('Failed to delete payment type');
      toastError('Failed to delete payment type');
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      Header: 'Name',
      accessor: 'name',
      sortable: true,
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      disableSortBy: true,
      Cell: ({ row }) => {
        const paymentType = row.original || row.values || row;
        const typeId = paymentType.id;
        const typeName = paymentType.name;

        return (
          <div
            className="action-buttons"
            style={{ display: 'flex', gap: '20px', alignItems: 'center' }}
          >
            <FaEdit
              style={{ cursor: 'pointer', color: '#153C7F', fontSize: '16px' }}
              onClick={() => handleEdit(paymentType)}
              title="Edit"
            />
            <FaTrash
              style={{ cursor: 'pointer', color: '#d32f2f', fontSize: '16px' }}
              onClick={() => handleDelete(typeId, typeName)}
              title="Delete"
            />
          </div>
        );
      },
    },
  ];

  if (showAddEdit) {
    return (
      <AddPaymentType
        type={editType}
        onClose={() => {
          setShowAddEdit(false);
          fetchPaymentTypes();
        }}
        embedded={embedded}
      />
    );
  }

  if (loading) {
    if (embedded) {
      return <RTSpinner />;
    }
    return (
      <RTPageLayout activePath={location.pathname}>
        <RTSpinner />
      </RTPageLayout>
    );
  }

  if (embedded) {
    return (
      <div style={{ background: 'transparent' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: '20px',
          }}
        >
          <RTButton requiresWrite variant="primary" onClick={handleCreate}>
            Add Payment Type
          </RTButton>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="table-container">
          <Table
            data={paymentTypes}
            columns={columns}
            exportable={false}
            showSearch={false}
            showTotalCount={true}
            className="payment-types-table"
          />
        </div>

        {/* Delete Loading Overlay */}
        {deleting && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
            }}
          >
            <RTSpinner
              size="medium"
              showMessage={true}
              message="Deleting payment type..."
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <RTPageLayout
      activePath={location.pathname}
      title="Payment Types"
      actions={
        <>
          <RTButton variant="secondary" onClick={() => navigate('/settings')}>
            &#8592; Back
          </RTButton>
          <RTButton requiresWrite variant="primary" onClick={handleCreate}>
            Add Payment Type
          </RTButton>
        </>
      }
    >
      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <Table
          data={paymentTypes}
          columns={columns}
          exportable={false}
          showSearch={false}
          showTotalCount={true}
          className="payment-types-table"
        />
      </div>

      {/* Delete Loading Overlay */}
      {deleting && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <RTSpinner
            size="medium"
            showMessage={true}
            message="Deleting payment type..."
          />
        </div>
      )}
    </RTPageLayout>
  );
};

export default PaymentTypesList;
